import { useEffect, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { Route as RootRoute } from "./__root";
import { useAuth } from "../providers/auth-context";
import { Input } from "@carry/ui";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/auth/invite",
  component: InviteRedeemPage,
});

const REDEEM_ENDPOINT = "/api/v1/invites/redeem";

const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "セッションが切れました。もう一度ログインしてください。",
  INVALID_BODY: "入力内容に誤りがあります。",
  INVALID_FORMAT: "招待コードの形式が正しくありません (英数字 8 桁)。",
  NOT_FOUND: "招待コードが見つかりません。",
  ALREADY_USED: "この招待コードは既に使用されています。",
  EXPIRED: "招待コードの有効期限が切れています。管理者に再発行を依頼してください。",
  REVOKED: "招待コードは取り消されています。",
  NO_CURRENT_USER: "現在のユーザーが見つかりません。",
  PLACEHOLDER_MISSING: "招待対象のユーザーが見つかりません。",
  RATE_LIMITED: "試行回数の上限に達しました。1 分ほど時間を置いてください。",
};

function resolveRedeemError(code: string | null | undefined, retryAfterSec?: number): string {
  if (!code) return "";
  if (code === "RATE_LIMITED" && retryAfterSec) {
    return `試行回数の上限に達しました。約 ${retryAfterSec} 秒後に再試行してください。`;
  }
  return ERROR_MESSAGES[code] ?? "招待コードの検証に失敗しました。";
}

function InviteRedeemPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      void navigate({ to: "/auth/login", search: { error: "no_session" } as { error: string } });
      return;
    }
    // status が既に active なら /
    const status = session.user.status;
    if (status === "active") {
      void navigate({ to: "/" });
    }
  }, [session, loading, navigate]);

  const form = useForm({
    defaultValues: {
      code: "",
    },
    onSubmit: async ({ value }) => {
      setError(null);
      try {
        const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080";
        const res = await fetch(`${baseUrl}${REDEEM_ENDPOINT}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: value.code.trim().normalize("NFKC").toUpperCase() }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          retryAfterSec?: number;
        };
        if (!res.ok) {
          setError(resolveRedeemError(body.error, body.retryAfterSec));
          return;
        }
        await navigate({ to: "/" });
        window.location.reload(); // session 情報を最新化するため簡易リロード
      } catch (e) {
        setError(e instanceof Error ? e.message : "ネットワークエラーが発生しました。");
      }
    },
  });

  return (
    <div className="max-w-md mx-auto mt-12 space-y-6">
      <h1 className="text-2xl font-semibold text-center">招待コードの入力</h1>
      <p className="text-sm text-neutral-600 text-center">
        管理者から発行された招待コードを入力してアカウントを有効化してください。
      </p>
      <div className="space-y-4">
        <div>
          <label htmlFor="invite-code" className="block text-xs font-medium text-neutral-700 mb-1">
            招待コード
          </label>
          <form.Field name="code">
            {(f) => (
              <Input
                id="invite-code"
                type="text"
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value)}
                autoComplete="off"
                autoFocus
                placeholder="例: ABCDEFGH"
                className="font-mono tracking-widest uppercase"
                maxLength={20}
                disabled={f.form.state.isSubmitting}
              />
            )}
          </form.Field>
        </div>
        {error && (
          <div
            className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700"
            role="alert"
          >
            {error}
          </div>
        )}
        <form.Subscribe selector={(s) => ({ isSubmitting: s.isSubmitting, code: s.values.code })}>
          {({ isSubmitting, code }) => (
            <button
              type="button"
              disabled={isSubmitting || code.trim().length === 0}
              className="w-full bg-neutral-900 text-white py-3 rounded-md font-medium disabled:opacity-50"
              onClick={() => form.handleSubmit()}
            >
              {isSubmitting ? "検証中…" : "招待コードを使う"}
            </button>
          )}
        </form.Subscribe>
      </div>
      <p className="text-xs text-neutral-500 text-center">
        招待コードの有効期限が切れている場合は、管理者に再発行を依頼してください。
      </p>
    </div>
  );
}

export { resolveRedeemError };
